import { formatDate } from './formatters';
import { openPrintWindow } from './openPrintWindow';

const fmtCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(amount || 0);
};

const generateBalanceSheetHTML = (balanceSheet) => {
  if (!balanceSheet) return '<p>No data available</p>';
  
  const renderSection = (title, items, total, totalLabel) => {
    if (!items || Object.keys(items).length === 0) return '';
    return `
      <div class="section">
        <div class="section-title">${title}</div>
        <table>
          <tbody>
            ${Object.entries(items).map(([name, amount]) => `
              <tr><td>${name}</td><td>${fmtCurrency(amount)}</td></tr>
            `).join('')}
            <tr class="subtotal-row">
              <td><strong>${totalLabel}</strong></td>
              <td><strong>${fmtCurrency(total)}</strong></td>
            </tr>
          </tbody>
        </table>
      </div>
    `;
  };
  
  return `
    <div class="report-body">
      <div class="major-section">ASSETS</div>
      ${renderSection('Current Assets', balanceSheet.current_assets, balanceSheet.total_current_assets, 'Total Current Assets')}
      ${renderSection('Fixed Assets', balanceSheet.fixed_assets, balanceSheet.total_fixed_assets, 'Total Fixed Assets')}
      ${renderSection('Other Assets', balanceSheet.other_assets, balanceSheet.total_other_assets, 'Total Other Assets')}
      <div class="total-row">
        <span>TOTAL ASSETS</span>
        <span>${fmtCurrency(balanceSheet.total_assets)}</span>
      </div>
      
      <div class="major-section">LIABILITIES</div>
      ${renderSection('Current Liabilities', balanceSheet.current_liabilities, balanceSheet.total_current_liabilities, 'Total Current Liabilities')}
      ${renderSection('Long-term Liabilities', balanceSheet.long_term_liabilities, balanceSheet.total_long_term_liabilities, 'Total Long-term Liabilities')}
      <div class="total-row">
        <span>TOTAL LIABILITIES</span>
        <span>${fmtCurrency(balanceSheet.total_liabilities)}</span>
      </div>
      
      ${renderSection('Equity', balanceSheet.equity, balanceSheet.total_equity, 'Total Equity')}
      
      <div class="total-row final">
        <span>TOTAL LIABILITIES & EQUITY</span>
        <span>${fmtCurrency(balanceSheet.total_liabilities_and_equity)}</span>
      </div>
    </div>
  `;
};

const generateIncomeStatementHTML = (incomeStatement) => {
  if (!incomeStatement) return '<p>No data available</p>';
  
  return `
    <div class="report-body">
      <div class="section">
        <div class="section-title">Income</div>
        <table>
          <tbody>
            ${Object.entries(incomeStatement.income || {}).map(([name, amount]) => `
              <tr><td>${name}</td><td>${fmtCurrency(amount)}</td></tr>
            `).join('') || '<tr><td colspan="2">No income recorded</td></tr>'}
            <tr class="subtotal-row">
              <td><strong>Total Income</strong></td>
              <td><strong>${fmtCurrency(incomeStatement.total_income)}</strong></td>
            </tr>
          </tbody>
        </table>
      </div>
      
      <div class="section">
        <div class="section-title">Expenses</div>
        <table>
          <tbody>
            ${Object.entries(incomeStatement.expenses || {}).sort((a, b) => a[0].localeCompare(b[0])).map(([name, amount]) => `
              <tr><td>${name}</td><td>${fmtCurrency(amount)}</td></tr>
            `).join('') || '<tr><td colspan="2">No expenses recorded</td></tr>'}
            <tr class="subtotal-row">
              <td><strong>Total Expenses</strong></td>
              <td><strong>${fmtCurrency(incomeStatement.total_expenses)}</strong></td>
            </tr>
          </tbody>
        </table>
      </div>
      
      <div class="total-row final">
        <span>NET INCOME</span>
        <span>${fmtCurrency(incomeStatement.net_income)}</span>
      </div>
    </div>
  `;
};

const generateGeneralLedgerHTML = (generalLedger) => {
  if (!generalLedger) return '<p>No data available</p>';
  
  const accountTypeNames = {
    'current_asset': 'Current Assets',
    'fixed_asset': 'Fixed Assets',
    'other_asset': 'Other Assets',
    'current_liability': 'Current Liabilities',
    'long_term_liability': 'Long-term Liabilities',
    'equity': 'Equity',
    'income': 'Income',
    'expense': 'Expenses'
  };
  
  let currentType = '';
  let html = '<div class="report-body ledger">';
  
  generalLedger.accounts.forEach(account => {
    if (account.account_type !== currentType) {
      currentType = account.account_type;
      html += `<div class="major-section">${accountTypeNames[currentType] || currentType}</div>`;
    }
    
    html += `
      <div class="account-section">
        <div class="account-header">
          <span class="account-name">${account.account_name}</span>
        </div>
        <table class="ledger-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Description</th>
              <th>Vendor/Payee</th>
              <th>Ref</th>
              <th class="amount">Debit</th>
              <th class="amount">Credit</th>
            </tr>
          </thead>
          <tbody>
            ${account.transactions.map(txn => `
              <tr>
                <td>${txn.date}</td>
                <td>${txn.description || '-'}</td>
                <td>${txn.vendor_name || '-'}</td>
                <td>${txn.reference || '-'}</td>
                <td class="amount">${txn.debit ? fmtCurrency(txn.debit) : ''}</td>
                <td class="amount">${txn.credit ? fmtCurrency(txn.credit) : ''}</td>
              </tr>
            `).join('')}
            <tr class="account-total">
              <td colspan="4"><strong>Account Total</strong></td>
              <td class="amount"><strong>${fmtCurrency(account.total_debits)}</strong></td>
              <td class="amount"><strong>${fmtCurrency(account.total_credits)}</strong></td>
            </tr>
          </tbody>
        </table>
      </div>
    `;
  });
  
  html += `
    <div class="grand-total">
      <span>GRAND TOTALS</span>
      <span class="totals">
        <span class="debit">${fmtCurrency(generalLedger.grand_total_debits)}</span>
        <span class="credit">${fmtCurrency(generalLedger.grand_total_credits)}</span>
      </span>
    </div>
  </div>`;
  
  return html;
};

const getLedgerStyles = (isLedger) => isLedger ? `
  .ledger { max-width: 700px; }
  .major-section { page-break-after: avoid; }
  .account-section { margin-bottom: 15px; }
  .account-header { background: #f0f0f0; padding: 4px 8px; font-weight: bold; font-size: 10pt; border: 1px solid #ccc; page-break-after: avoid; }
  .ledger-table { width: 100%; border-collapse: collapse; font-size: 8pt; }
  .ledger-table th { background: #e0e0e0; padding: 3px 5px; text-align: left; border: 1px solid #ccc; }
  .ledger-table td { padding: 2px 5px; border: 1px solid #eee; }
  .ledger-table th.amount, .ledger-table td.amount { text-align: right; width: 80px; }
  .account-total td { border-top: 2px solid #000; background: #f9f9f9; }
  .grand-total { display: flex; justify-content: space-between; padding: 10px; margin-top: 15px; font-size: 11pt; font-weight: bold; border: 2px solid #000; background: #f0f0f0; page-break-inside: avoid; }
  .grand-total .totals { display: flex; gap: 30px; }
  .grand-total .debit, .grand-total .credit { min-width: 80px; text-align: right; }
` : '';

export function printReport({ reportType, llcName, balanceSheet, incomeStatement, generalLedger, startDate, endDate, balanceAsOfDate }) {
  const reportTitle = reportType === 'balance' ? 'Balance Sheet' : (reportType === 'income' ? 'Income Statement' : 'General Ledger');
  
  const runDate = new Date().toLocaleDateString('en-US', { 
    timeZone: 'America/Chicago',
    month: 'long', 
    day: 'numeric', 
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  let bodyHTML;
  if (reportType === 'balance') {
    bodyHTML = generateBalanceSheetHTML(balanceSheet);
  } else if (reportType === 'income') {
    bodyHTML = generateIncomeStatementHTML(incomeStatement);
  } else {
    bodyHTML = generateGeneralLedgerHTML(generalLedger);
  }
  
  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>${llcName} - ${reportTitle}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: Arial, sans-serif; font-size: 10pt; line-height: 1.3; color: #000; }
        
        .page-table { width: 100%; border-collapse: collapse; }
        .page-table > thead td, .page-table > tfoot td { padding: 0; }
        
        .header { text-align: center; margin-bottom: 15px; padding-bottom: 10px; border-bottom: 2px solid #000; }
        .header h1 { font-size: 14pt; font-weight: bold; margin-bottom: 3px; }
        .header h2 { font-size: 12pt; font-weight: bold; margin-bottom: 3px; }
        .header .date { font-size: 9pt; color: #333; }
        
        .footer-content { border-top: 1px solid #999; padding: 4px 0 0 0; margin-top: 8px; font-size: 7pt; color: #666; }
        .footer-row { display: flex; justify-content: space-between; align-items: center; }
        .footer-content .confidential { font-weight: bold; color: #333; }
        
        .report-body { max-width: 500px; margin: 0 auto; }
        .major-section { font-size: 10pt; font-weight: bold; padding: 5px 0; margin-top: 12px; margin-bottom: 3px; border-bottom: 1px solid #000; page-break-after: avoid; }
        .section { margin-bottom: 8px; page-break-inside: avoid; }
        .section-title { font-size: 9pt; font-weight: bold; padding: 4px 0 2px 10px; }
        .report-body table, .ledger table, .section table { width: 100%; border-collapse: collapse; }
        .report-body table td, .section table td { padding: 2px 0; font-size: 9pt; }
        .report-body table td:first-child, .section table td:first-child { padding-left: 20px; }
        .report-body table td:last-child, .section table td:last-child { text-align: right; width: 100px; }
        .subtotal-row td { padding-top: 3px; border-top: 1px solid #000; }
        .total-row { display: flex; justify-content: space-between; padding: 5px 0; margin-top: 5px; font-size: 10pt; font-weight: bold; border-top: 1px solid #000; }
        .total-row.final { margin-top: 10px; border-top: 2px solid #000; padding-top: 8px; }
        
        ${getLedgerStyles(reportType === 'ledger')}
        
        @page { 
          margin: 0.4in 0.5in 0.6in 0.5in; 
          size: letter;
          @bottom-center {
            content: "Page " counter(page) " of " counter(pages);
            font-size: 7pt;
            color: #666;
            font-family: Arial, sans-serif;
          }
        }
        @media print { 
          body { padding: 0; }
        }
      </style>
    </head>
    <body>
      <table class="page-table">
        <thead>
          <tr><td>
            <div class="header">
              <h1>${llcName}</h1>
              <h2>${reportTitle}</h2>
              <div class="date">
                ${reportType === 'balance' 
                  ? `As of ${formatDate(balanceAsOfDate)}`
                  : `For the period ${formatDate(startDate)} to ${formatDate(endDate)}`
                }
              </div>
            </div>
          </td></tr>
        </thead>
        <tfoot>
          <tr><td>
            <div class="footer-content">
              <div class="footer-row">
                <span>Report generated: ${runDate}</span>
                <span class="confidential">CONFIDENTIAL FINANCIAL INFORMATION</span>
              </div>
            </div>
          </td></tr>
        </tfoot>
        <tbody>
          <tr><td>
            ${bodyHTML}
          </td></tr>
        </tbody>
      </table>
    </body>
    </html>
  `;
  openPrintWindow(html);
}
